import { use, useEffect, useRef, useState } from "react";
import Sidebar from "../../customComponents/Sidebar/Sidebar";
import {
  checkin,
  getCheckinActivity,
  getEventLogs,
  getUncheckedParticipantsLogs,
} from "./apiRoutes/events";
import {
  ChevronRight,
  Loader2,
  TriangleAlert,
  CircleCheckBig,
  X,
  CheckCircle,
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Box,
  Container,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  TextField,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputLabel,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { MetricChart, MetricStat } from "./pageComponents/ChartComponents";
import { getEventMeta } from "./apiRoutes/eventMeta";

const query = new URLSearchParams(window.location.search);

const baseColors = [
  "rgba(255, 99, 132, 0.6)",
  "rgba(54, 162, 235, 0.6)",
  "rgba(255, 206, 86, 0.6)",
  "rgba(75, 192, 192, 0.6)",
  "rgba(153, 102, 255, 0.6)",
  "rgba(255, 159, 64, 0.6)",
];

/**
 * "8+" is a legitimate stored value, and parseInt reads it as 8.
 */
const toCount = (value) => {
  const parsed = parseInt(value, 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Group an answer by what it means, not by its exact stored text.
 *
 * The stored value is whatever the choice label said at the time it was
 * submitted, and labels get edited - a Vietnamese translation was added beside
 * each English one mid-season. Without this, "Maybe" and "Maybe (Có thể)"
 * count as two different answers and the chart draws a slice for each.
 *
 * Dash variants are folded too: the age buckets contain both "6–8" with an en
 * dash and "6-8" with a plain hyphen, which split the same bucket in two.
 */
/**
 * Choices that were rewritten rather than translated.
 *
 * Stripping a bracketed translation merges most renames on its own, but
 * "VietTV / Media" became "VietTV / Truyền thông" - no shared suffix, nothing
 * to detect - so the pair has to be stated. Keyed by the old wording, pointing
 * at the current one. Add a line here whenever a choice is reworded outright,
 * or its historical rows will chart as a separate slice.
 */
const LABEL_ALIASES = {
  "VietTV / Media": "VietTV / Truyền thông",
};

const canonicalLabel = (value) => {
  const normalised = String(value ?? "")
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  return LABEL_ALIASES[normalised] || normalised;
};

/**
 * Current choice labels for one form field, keyed by their canonical form.
 *
 * Localised by the shortcode. Read defensively: this page also reads
 * tmtAdminPortal, which a different shortcode owns, so nothing here should
 * assume a particular object is present.
 */
const currentLabels = (fieldKey) => {
  const choices =
    (typeof tmtMetrics !== "undefined" && tmtMetrics?.choices?.[fieldKey]) || [];
  const lookup = {};

  choices.forEach((label) => {
    lookup[canonicalLabel(label)] = label;
  });

  return lookup;
};

/**
 * Relabel a tally's keys with what the form calls them today, leaving the
 * counts alone. A bucket the form no longer offers keeps its stored wording
 * rather than disappearing.
 */
const withCurrentLabels = (bucket, fieldKey) => {
  const lookup = currentLabels(fieldKey);
  const output = {};

  Object.keys(bucket).forEach((key) => {
    const label = lookup[key] || key;

    output[label] = (output[label] || 0) + bucket[key];
  });

  return output;
};

const isCheckedIn = (log) => parseInt(log.status, 10) === 1;

/* The venue's timezone. Check-in times are stored in UTC. */
const EVENT_TZ = "America/Chicago";

const CHECKIN_INTERVALS = [
  { value: 60, label: "1 hour" },
  { value: 30, label: "30 min" },
  { value: 15, label: "15 min" },
];

/**
 * Beyond this many bars the chart is unreadable, so empty intervals stop being
 * filled in. Reached only when check-ins are spread over days, which is test
 * data rather than an event night.
 */
const MAX_TIMELINE_BARS = 200;

const tzParts = new Intl.DateTimeFormat("en-CA", {
  timeZone: EVENT_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * A UTC timestamp as wall-clock time at the venue.
 *
 * Done through Intl rather than a fixed offset so the CDT/CST changeover is
 * handled for us - October is CDT, November onwards is CST.
 */
const toEventTime = (utc) => {
  if (!utc) {
    return null;
  }

  const date = new Date(`${String(utc).replace(" ", "T")}Z`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = Object.fromEntries(
    tzParts
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  };
};

const clockLabel = (hour, minute) => {
  const suffix = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;

  return `${display}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Check-ins per interval, in venue time.
 *
 * Empty intervals are kept so the shape of the evening is honest - a lull
 * between rushes should read as a gap, not be closed up.
 */
const buildCheckinTimeline = (rows, minutes) => {
  const stamps = rows
    .map((row) => toEventTime(row.created_utc))
    .filter(Boolean)
    .map((time) => ({
      ...time,
      /* Minutes since epoch-ish, for ordering and gap filling. */
      slot: Math.floor(
        (Date.UTC(time.year, time.month - 1, time.day, time.hour, time.minute) /
          60000 /
          minutes)
      ),
    }))
    .sort((a, b) => a.slot - b.slot);

  if (stamps.length === 0) {
    return { bucket: {}, total: 0, busiest: null };
  }

  const counts = new Map();

  stamps.forEach((time) => {
    counts.set(time.slot, (counts.get(time.slot) || 0) + 1);
  });

  const first = stamps[0].slot;
  const last = stamps[stamps.length - 1].slot;
  const span = last - first + 1;

  const slots =
    span <= MAX_TIMELINE_BARS
      ? Array.from({ length: span }, (_, index) => first + index)
      : [...counts.keys()].sort((a, b) => a - b);

  /* Only show the date when the check-ins straddle more than one day. */
  const multiDay =
    new Set(stamps.map((time) => `${time.month}-${time.day}`)).size > 1;

  const bucket = {};
  let busiest = null;

  slots.forEach((slot) => {
    const at = new Date(slot * minutes * 60000);
    const hour = at.getUTCHours();
    const minute = at.getUTCMinutes();
    const time = clockLabel(hour, minute);
    const label = multiDay
      ? `${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()}, ${time}`
      : time;
    const count = counts.get(slot) || 0;

    bucket[label] = count;

    if (!busiest || count > busiest.count) {
      busiest = { label, count };
    }
  });

  return { bucket, total: stamps.length, busiest };
};

const share = (part, whole) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

/**
 * The post-event report: who registered, who actually came, and by which
 * route.
 *
 * Deliberately built from every registration for the event, ignoring the
 * All / Checked-in toggle. Filtering to checked-in would make the turnout
 * figures read 100% and the registration counts collapse onto the attendance
 * ones, which is exactly backwards for a report.
 */
/**
 * Heads, not registrations: one booking admits a whole party.
 *
 * "8+" is a real stored value for these counts, which toCount() reads as 8 -
 * so a very large party is undercounted rather than dropped.
 */
const headcount = (logs) =>
  logs.reduce(
    (total, log) => {
      const meta = log.metadata || {};

      total.adults += toCount(meta.adult_count);
      total.children += toCount(meta.child_count);

      return total;
    },
    { adults: 0, children: 0 }
  );

/* The questions whose charts report how many registrations answered them. */
const RESPONSE_FIELDS = [
  "first_time_attendee",
  "is_tmt_customer",
  "volunteer_interest",
];

const buildReport = (logs) => {
  const preReg = logs.filter((log) => !log.is_walk_in);
  const walkIn = logs.filter((log) => log.is_walk_in);

  const attendedLogs = logs.filter(isCheckedIn);

  const checkedIn = attendedLogs.length;
  const preRegCheckedIn = preReg.filter(isCheckedIn).length;
  const walkInCheckedIn = walkIn.filter(isCheckedIn).length;

  const responses = Object.fromEntries(
    RESPONSE_FIELDS.map((key) => {
      const answered = logs.filter(
        (log) => canonicalLabel((log.metadata || {})[key]) !== ""
      ).length;

      return [
        key,
        {
          answered,
          ignored: logs.length - answered,
          rate: share(answered, logs.length),
          /* Derived, not 100 - rate: that leaves floating point dust. */
          ignoredRate: share(logs.length - answered, logs.length),
        },
      ];
    })
  );

  const registeredPeople = headcount(logs);
  const attendedPeople = headcount(attendedLogs);

  const registeredTotal = registeredPeople.adults + registeredPeople.children;
  const attendedTotal = attendedPeople.adults + attendedPeople.children;

  return {
    responses,
    people: {
      registeredAdults: registeredPeople.adults,
      registeredChildren: registeredPeople.children,
      registeredTotal,

      attendedAdults: attendedPeople.adults,
      attendedChildren: attendedPeople.children,
      attendedTotal,

      /* Of the heads expected, how many came through the door. */
      turnoutRate: share(attendedTotal, registeredTotal),

      /* Composition of the crowd that actually attended. */
      adultShare: share(attendedPeople.adults, attendedTotal),
      childShare: share(attendedPeople.children, attendedTotal),
    },

    registrations: logs.length,
    preRegistrations: preReg.length,
    walkInRegistrations: walkIn.length,

    checkedIn,
    preRegCheckedIn,
    walkInCheckedIn,

    /* Turnout: of everyone registered, how many came through the door. */
    turnoutRate: share(checkedIn, logs.length),

    /* Composition: of those who came, how they had registered. */
    preRegShareOfCheckIns: share(preRegCheckedIn, checkedIn),
    walkInShareOfCheckIns: share(walkInCheckedIn, checkedIn),

    /* Follow-through: of those who booked ahead, how many turned up. */
    preRegTurnout: share(preRegCheckedIn, preReg.length),

    preRegShare: share(preReg.length, logs.length),
    walkInShare: share(walkIn.length, logs.length),
    noShows: preReg.length - preRegCheckedIn,
  };
};

const tally = (bucket, value) => {
  if (!value) {
    return;
  }

  bucket[value] = (bucket[value] || 0) + 1;
};

/**
 * Roll up the per-registration metadata captured from the registration form.
 */
const aggregateFormMetrics = (logs) => {
  const metrics = {
    adults: 0,
    children: 0,
    registrations: logs.length,
    checkedIn: 0,
    referralSources: {},
    firstTime: { Yes: 0, No: 0 },
    tmtCustomer: { Yes: 0, No: 0, Undefined: 0 },
    volunteerInterest: {},
    childAgeGroups: {},
  };

  logs.forEach((log) => {
    const meta = log.metadata || {};

    if (parseInt(log.status, 10) === 1) {
      metrics.checkedIn += 1;
    }

    metrics.adults += toCount(meta.adult_count);
    metrics.children += toCount(meta.child_count);

    tally(metrics.referralSources, canonicalLabel(meta.referral_source));
    tally(metrics.volunteerInterest, canonicalLabel(meta.volunteer_interest));

    const firstTime = canonicalLabel(meta.first_time_attendee);

    if (firstTime === "Yes" || firstTime === "No") {
      metrics.firstTime[firstTime] += 1;
    }

    /*
     * Unanswered is counted, not dropped: the field was added mid-season, so
     * a blank is "we never asked" and that is a real share of the figures.
     */
    const customer = canonicalLabel(meta.is_tmt_customer);

    metrics.tmtCustomer[
      customer === "Yes" || customer === "No" ? customer : "Undefined"
    ] += 1;

    /*
     * Stored as a comma separated list of the buckets they ticked. Normalised
     * before the split, so a translation containing a comma cannot be read as
     * an extra bucket.
     */
    canonicalLabel(meta.child_age_groups)
      .split(",")
      .map((group) => group.trim())
      .filter((group) => group && group !== "Not Applicable")
      .forEach((group) => tally(metrics.childAgeGroups, group));
  });

  return metrics;
};

function EventMetrics() {
  const isEventLogsSet = useRef(false);
  const isEventMetaSet = useRef(false);
  const [originalEventLogs, setOriginalEventLogs] = useState([]);
  const [originalEventMeta, setOriginalEventMeta] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState({
    name: "",
    id: "-1",
  });
  const [events, setEvents] = useState([]);
  const [ageGroups, setAgeGroups] = useState({});
  const [demographicCount, setDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [eventLogDemographicCount, setEventLogDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [eventMetaDemographicCount, setEventMetaDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [accountRecency, setAccountRecency] = useState({
    new_accounts: 0,
    old_accounts: 0,
  });

  /**
   * Metrics derived from the registration form itself, rather than inferred
   * from user profiles. Form registrants have no age on their account, so the
   * age-based split alone would misclassify all of them.
   */
  /** "all" counts everyone registered; "checked_in" only those who arrived. */
  const [attendanceFilter, setAttendanceFilter] = useState("all");

  /*
   * Always computed over the event's full log set, so these two stay
   * meaningful no matter how the rest of the page is filtered.
   */
  const [attendanceSummary, setAttendanceSummary] = useState({
    registered: 0,
    checkedIn: 0,
  });

  const [report, setReport] = useState(buildReport([]));
  const [checkinActivity, setCheckinActivity] = useState([]);
  const [checkinInterval, setCheckinInterval] = useState(60);
  const [breakdownOpen, setBreakdownOpen] = useState(true);
  const [pctShown, setPctShown] = useState({
    registrations: false,
    attendance: false,
    headcount: false,
  });

  const togglePct = (key) =>
    setPctShown((shown) => ({ ...shown, [key]: !shown[key] }));


  const [formMetrics, setFormMetrics] = useState({
    adults: 0,
    children: 0,
    registrations: 0,
    checkedIn: 0,
    referralSources: {},
    firstTime: { Yes: 0, No: 0 },
    tmtCustomer: { Yes: 0, No: 0, Undefined: 0 },
    volunteerInterest: {},
    childAgeGroups: {},
  });

  const isAdmin = !!tmtMetrics.is_admin;

  if (!isAdmin) {
    window.location.href = "portal-login";
    return null;
  }

  useEffect(() => {
    (async () => {
      // Fetch once and hand the same rows to both aggregations.
      const eventLogs =
        originalEventLogs.length > 0 ? originalEventLogs : await getEventLogs();

      initializeEventLogs(eventLogs);
      initalizeEventMeta(eventLogs);
    })();
  }, [selectedEvent, attendanceFilter]);

  useEffect(() => {
    initializeEventOptions();
  }, [originalEventLogs]);

  const initializeEventOptions = () => {
    const eventSet = new Set();
    const newEventOptions = [];

    originalEventLogs.forEach((val) => {
      const eventId = val.event_id;
      if (!eventSet.has(eventId)) {
        eventSet.add(eventId);
        newEventOptions.push({
          name: val.event_title,
          id: val.event_id,
        });
      }
    });

    if (newEventOptions.length > 0) {
      setSelectedEvent(newEventOptions[0]);
    }
    setEvents(newEventOptions);
  };

  /** A log counts towards the charts only if it passes the attendance toggle. */
  const matchesAttendance = (log) =>
    attendanceFilter === "all" || parseInt(log.status, 10) === 1;

  const initializeEventLogs = async (eventLogs) => {
    const eventSet = new Set();
    const newEventOptions = [];
    const newAgeGroups = [];
    const newAccountRecencies = {
      new_accounts: 0,
      old_accounts: 0,
    };
    const newEventLogDemographics = {
      adult_count: 0,
      child_count: 0,
    };
    const newEventMetaDemographic = {
      adult_count: 0,
      child_count: 0,
    };

    const eventLogsForEvent = eventLogs.filter(
      (val) => val.event_title === selectedEvent.name
    );

    setAttendanceSummary({
      registered: eventLogsForEvent.length,
      checkedIn: eventLogsForEvent.filter(
        (val) => parseInt(val.status, 10) === 1
      ).length,
    });

    setReport(buildReport(eventLogsForEvent));

    /*
     * The timeline comes from the activity log rather than checkin_at, which
     * a repeat scan overwrites - that would move someone from the hour they
     * were admitted to the hour they were rescanned.
     */
    const eventId = parseInt(eventLogsForEvent[0]?.event_id ?? 0, 10);

    getCheckinActivity(eventId)
      .then((response) =>
        setCheckinActivity(
          (response.rows ?? []).filter((row) => row.action === "checkin")
        )
      )
      .catch(() => setCheckinActivity([]));

    const selectedLogs = eventLogsForEvent.filter(matchesAttendance);

    setFormMetrics(aggregateFormMetrics(selectedLogs));

    selectedLogs
      .forEach((val) => {
        const eventId = val.event_id;
        const age = (val.user?.age ?? "").toString();

        if (parseInt(val.is_new_account)) {
          newAccountRecencies.new_accounts += 1;
        } else {
          newAccountRecencies.old_accounts += 1;
        }

        /*
         * Registrations that carry their own adult/child counts are already
         * summed through the event metadata, so inferring head counts from the
         * account age here as well would count them twice.
         */
        const meta = val.metadata || {};
        if (meta.adult_count || meta.child_count) {
          return;
        }

        if (age === "") {
          newEventMetaDemographic.adult_count += 1;
          return;
        }

        if (age >= 18) {
          newEventLogDemographics.adult_count += 1;
        } else {
          newEventLogDemographics.child_count += 1;
        }

        if (!eventSet.has(eventId)) {
          eventSet.add(eventId);
          newEventOptions.push({
            name: val.event_title,
            id: val.event_id,
          });
        }

        if (newAgeGroups[age]) {
          newAgeGroups[age] += 1;
        } else {
          newAgeGroups[age] = 1;
        }
      });

    /*
     * Guard on this fetch's own ref. Checking the metadata ref here meant the
     * logs were dropped whenever the metadata request happened to resolve
     * first, which left the event dropdown empty at random.
     */
    if (!isEventLogsSet.current) {
      isEventLogsSet.current = true;
      setOriginalEventLogs(eventLogs);
    }

    setEventLogDemographicCount(newEventLogDemographics);

    /*
     * Always publish this, even when it is zero. It used to be set only from
     * inside the loop, so an event with no age-less registrants kept the
     * previous event's figure and the Adult Count came out too high.
     */
    setEventMetaDemographicCount(newEventMetaDemographic);
    setAccountRecency(newAccountRecencies);
    setAgeGroups(newAgeGroups);
  };

  const initalizeEventMeta = async (eventLogs) => {
    const newEventMeta =
      originalEventMeta.length > 0 ? originalEventMeta : await getEventMeta();
    const acceptedMetaKeys = ["adult_count", "child_count"];
    const newDemographicCount = { adult_count: 0, child_count: 0 };

    /*
     * Metadata carries its own counts, so it has to respect the toggle too or
     * the tiles would mix checked-in logs with everyone's party sizes.
     * Legacy rows have log_id 0 and cannot be traced to a person, so they are
     * left out once we are reporting on attendance.
     */
    const allowedLogIds = new Set(
      (eventLogs || [])
        .filter(
          (val) => val.event_title === selectedEvent.name && matchesAttendance(val)
        )
        .map((val) => parseInt(val.id, 10))
    );

    newEventMeta
      .filter((val) => val.event_id === selectedEvent.id)
      .forEach((val) => {
        const metaKey = val.meta_key;
        const count = val.meta_value;

        if (!acceptedMetaKeys.includes(metaKey)) {
          return;
        }

        if (
          attendanceFilter !== "all" &&
          !allowedLogIds.has(parseInt(val.log_id, 10))
        ) {
          return;
        }

        newDemographicCount[metaKey] += parseInt(count);
      });

    if (!isEventMetaSet.current) {
      isEventMetaSet.current = true;
      setOriginalEventMeta(newEventMeta);
    }

    setDemographicCount(newDemographicCount);
  };

  const generateUniqueColors = (count) => {
    return Array.from({ length: count }, (_, i) => {
      const hue = Math.floor((360 / count) * i); // even spacing
      return `hsla(${hue}, 70%, 60%, 0.6)`; // nice bright colors
    });
  };

  const ageGroupLabels = ["Adult", "Child", ...Object.keys(ageGroups)];
  const ageGroupData = [
    demographicCount.adult_count + eventMetaDemographicCount.adult_count,
    demographicCount.child_count + eventMetaDemographicCount.child_count,
    ...Object.keys(ageGroups).map((key) => ageGroups[key]),
  ];

  const ageData = {
    labels: ageGroupLabels,
    datasets: [
      {
        label: "Demographic",
        data: ageGroupData,
        backgroundColor: generateUniqueColors(ageGroupLabels.length),
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  /* Counts in the legend; the slices themselves carry the share. */
  const countLegendLabels = (chart) =>
    chart.data.labels.map((label, index) => {
      const style = chart.getDatasetMeta(0).controller.getStyle(index);

      return {
        text: `${label} (${chart.data.datasets[0].data[index]})`,
        fillStyle: style.backgroundColor,
        strokeStyle: style.borderColor,
        lineWidth: style.borderWidth,
        hidden: !chart.getDataVisibility(index),
        index,
      };
    });

  const ageOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: { generateLabels: countLegendLabels },
      },
      title: {
        display: true,
        text: "Demographic",
        align: "start",
        color: "#000000", // 🔹 change title color
        font: {
          size: 18, // 🔹 increase title font size
          weight: "bold",
          family: "Arial", // optional
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed;
            const dataset = context.chart.data.datasets[context.datasetIndex];
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 1000) / 10; // nearest tenth
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };

  /**
   * Build a chart config from a { label: count } bucket.
   */
  const distributionChart = (bucket, title, type = "bar", palette = null) => {
    const labels = Object.keys(bucket);
    const values = labels.map((label) => bucket[label]);

    return {
      hasData: values.some((value) => value > 0),
      data: {
        labels,
        datasets: [
          {
            label: title,
            data: values,
            backgroundColor: palette
              ? labels.map((label) => palette[label] ?? "rgba(158, 158, 158, 0.55)")
              : generateUniqueColors(labels.length || 1),
            borderColor: "#fff",
            borderWidth: type === "pie" ? 2 : 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: type === "pie",
            position: "top",
            labels: { generateLabels: countLegendLabels },
          },
          title: {
            display: true,
            text: title,
            align: "start",
            color: "#000000",
            font: { size: 18, weight: "bold", family: "Arial" },
          },
        },
        ...(type === "bar"
          ? { scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
          : {}),
      },
    };
  };

  const referralChart = distributionChart(
    withCurrentLabels(formMetrics.referralSources, "referral_source"),
    "How They Heard About Us"
  );
  /* Yes/No is relabelled for meaning here, so it does not take form wording. */
  const customerChart = distributionChart(
    Object.fromEntries(
      Object.entries(formMetrics.tmtCustomer ?? {}).filter(([, count]) => count > 0)
    ),
    "TMT Customers",
    "pie",
    {
      Yes: "rgba(46, 125, 50, 0.75)",
      No: "rgba(211, 47, 47, 0.75)",
      Undefined: "rgba(158, 158, 158, 0.55)",
    }
  );
  const firstTimeChart = distributionChart(
    { "First time": formMetrics.firstTime.Yes, Returning: formMetrics.firstTime.No },
    "First Time vs Returning",
    "pie"
  );
  const volunteerChart = distributionChart(
    withCurrentLabels(formMetrics.volunteerInterest, "volunteer_interest"),
    "Volunteer Interest",
    "pie"
  );
  const childAgeChart = distributionChart(
    withCurrentLabels(formMetrics.childAgeGroups, "child_age_groups"),
    "Ages of Children Attending"
  );

  const responseCaption = (key) => {
    const item = report.responses?.[key];

    if (!item) {
      return "";
    }

    return (
      `${item.answered} responded (${item.rate}%) · ` +
      `${item.ignored} ignored (${item.ignoredRate}%)`
    );
  };

  const checkinTimeline = buildCheckinTimeline(
    checkinActivity,
    checkinInterval
  );

  /* Repeat scans are real door events, but worth naming separately. */
  const repeatScans = checkinActivity.filter((row) =>
    String(row.note || "").startsWith("Repeat")
  ).length;

  const checkinTimelineChart = {
    hasData: checkinTimeline.total > 0,
    data: {
      labels: Object.keys(checkinTimeline.bucket),
      datasets: [
        {
          label: "Check-ins",
          data: Object.values(checkinTimeline.bucket),
          backgroundColor: "rgba(45, 82, 176, 0.65)",
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) =>
              `${item.parsed.y} check-in${item.parsed.y === 1 ? "" : "s"}`,
          },
        },
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } },
        x: {
          ticks: {
            autoSkip: true,
            maxRotation: 90,
            minRotation: 45,
          },
        },
      },
    },
  };

  /* share() rather than its own rounding, so the tile and the report agree. */
  const checkInRate = share(
    attendanceSummary.checkedIn,
    attendanceSummary.registered
  );

  return (
    <div className="admin-portal-container">
      <Sidebar selectedIndex={0} />
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="start"
        sx={{
          pt: 2,
          width: "100%",
          px: { sm: 2, lg: 4 },
        }}
        gap={4}
      >
        <div className="metrics-toolbar">
          <Box sx={{ minWidth: 250 }}>
            <Autocomplete
              disablePortal
              size="small"
              value={
                selectedEvent.name === ""
                  ? { name: "Select Event" }
                  : events.find(
                      (m) =>
                        m.name.toLowerCase() === selectedEvent.name.toLowerCase()
                    ) || null
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedEvent(
                    newValue.name === "Select Event"
                      ? { name: "", id: "" }
                      : newValue
                  );
                }
              }}
              options={[{ name: "Select Event" }, ...events]}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Event"
                  variant="outlined"
                />
              )}
            />
          </Box>

          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={attendanceFilter}
              onChange={(e, v) => v && setAttendanceFilter(v)}
            >
              <ToggleButton value="all">All registered</ToggleButton>
              <ToggleButton value="checked_in">Checked in only</ToggleButton>
            </ToggleButtonGroup>

            {attendanceFilter === "checked_in" && (
              <Typography variant="body2" sx={{ color: "#666" }}>
                Showing only the {attendanceSummary.checkedIn} registration
                {attendanceSummary.checkedIn === 1 ? "" : "s"} that were checked
                in. Counts still come from what was entered on the form.
              </Typography>
            )}
          </Stack>
        </div>

        <div className="event-metrics-container">
          <Box
            className="metric-pie"
            sx={{
              // height: 400,
              // gridColumn: "span 2",
              gridRow: "span 2",
            }}
          >
            <MetricChart data={ageData} options={ageOptions} type={"pie"} />
          </Box>

          <Box className="metric-tile" sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Registrations"}
              value={attendanceSummary.registered}
            />
          </Box>
          <Box className="metric-tile" sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Checked In"}
              value={`${attendanceSummary.checkedIn} (${checkInRate}%)`}
            />
          </Box>
          <Box className="metric-tile" sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Adult Count"}
              value={
                demographicCount.adult_count +
                eventLogDemographicCount.adult_count +
                eventMetaDemographicCount.adult_count
              }
            />
          </Box>
          <Box className="metric-tile" sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Child Count"}
              value={
                demographicCount.child_count +
                eventLogDemographicCount.child_count +
                eventMetaDemographicCount.child_count
              }
            />
          </Box>
        </div>

        {(referralChart.hasData ||
          customerChart.hasData ||
          firstTimeChart.hasData ||
          volunteerChart.hasData ||
          childAgeChart.hasData) && (
          <div className="event-metrics-charts">
            {referralChart.hasData && (
              <div className="chart-wide">
                <MetricChart
                  data={referralChart.data}
                  options={referralChart.options}
                  type={"bar"}
                />
              </div>
            )}
            {firstTimeChart.hasData && (
              <div className="chart-half">
                <MetricChart
                  data={firstTimeChart.data}
                  options={firstTimeChart.options}
                  type={"pie"}
                  caption={responseCaption("first_time_attendee")}
                />
              </div>
            )}
            {customerChart.hasData && (
              <div className="chart-half">
                <MetricChart
                  data={customerChart.data}
                  options={customerChart.options}
                  type={"pie"}
                  caption={responseCaption("is_tmt_customer")}
                />
              </div>
            )}
            {volunteerChart.hasData && (
              <div className="chart-half">
                <MetricChart
                  data={volunteerChart.data}
                  options={volunteerChart.options}
                  type={"pie"}
                  caption={responseCaption("volunteer_interest")}
                />
              </div>
            )}
            {childAgeChart.hasData && (
              <div className="chart-wide">
                <MetricChart
                  data={childAgeChart.data}
                  options={childAgeChart.options}
                  type={"bar"}
                />
              </div>
            )}
          </div>
        )}

        <section className="metrics-breakdown">
          <button
            type="button"
            className="metrics-breakdown__toggle"
            aria-expanded={breakdownOpen}
            onClick={() => setBreakdownOpen((open) => !open)}
          >
            <ChevronRight
              size={20}
              className="metrics-breakdown__chevron"
              aria-hidden="true"
            />
            <span>
              <span className="metrics-breakdown__title">Breakdown</span>
              <span className="metrics-breakdown__note">
                When people arrived, how they registered, and how many came
                through the door &mdash; always across all{" "}
                {report.registrations} registrations, whatever the filter above
                is set to
              </span>
            </span>
          </button>

          {breakdownOpen && (
            <div className="metrics-breakdown__body">
          {/*
            * Post-event report. Always reflects every registration for the
            * event, whatever the All / Checked-in toggle is set to - the note
            * on each panel says so, because the toggle visibly changes the
            * charts right above it.
            */}
          <div className="metrics-report">
            <section className="report-panel">
              <header className="report-panel__head">
                <div className="report-panel__heading">
                  <h3>Registrations</h3>
                  <Button
                    size="small"
                    onClick={() => togglePct("registrations")}
                  >
                    {pctShown.registrations ? "Hide %" : "Show %"}
                  </Button>
                </div>
                <p className="report-panel__note">
                  How people signed up, whether or not they attended
                </p>
              </header>

              <div className="report-row report-row--total">
                <span className="report-row__label">Total registrations</span>
                <span className="report-row__value">{report.registrations}</span>
                {pctShown.registrations && (
                  <span className="report-row__pct report-row__pct--none">100%</span>
                )}
                <span className="report-row__note">
                  {report.preRegistrations} pre-registered &middot;{" "}
                  {report.walkInRegistrations} walk-in
                  {report.walkInRegistrations === 1 ? "" : "s"}
                </span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Pre-registered</span>
                <span className="report-row__value">
                  {report.preRegistrations}
                </span>
                {pctShown.registrations && (
                  <span className="report-row__pct">{report.preRegShare}%</span>
                )}
                <span className="report-row__note">of all registrations</span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Walk-ins</span>
                <span className="report-row__value">
                  {report.walkInRegistrations}
                </span>
                {pctShown.registrations && (
                  <span className="report-row__pct">{report.walkInShare}%</span>
                )}
                <span className="report-row__note">of all registrations</span>
              </div>
            </section>

            <section className="report-panel">
              <header className="report-panel__head">
                <div className="report-panel__heading">
                  <h3>Attendance</h3>
                  <Button
                    size="small"
                    onClick={() => togglePct("attendance")}
                  >
                    {pctShown.attendance ? "Hide %" : "Show %"}
                  </Button>
                </div>
                <p className="report-panel__note">
                  All {report.registrations} registrations, regardless of the
                  filter above
                </p>
              </header>

              <div className="report-row report-row--total">
                <span className="report-row__label">Total checked in</span>
                <span className="report-row__value">{report.checkedIn}</span>
                {pctShown.attendance && (
                  <span className="report-row__pct">{report.turnoutRate}%</span>
                )}
                <span className="report-row__note">
                  of {report.registrations} registered
                </span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Pre-registered</span>
                <span className="report-row__value">{report.preRegCheckedIn}</span>
                {pctShown.attendance && (
                  <span className="report-row__pct">{report.preRegShareOfCheckIns}%</span>
                )}
                <span className="report-row__note">
                  of all check-ins &middot; {report.preRegTurnout}% of the{" "}
                  {report.preRegistrations} who booked ahead turned up
                </span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Walk-ins</span>
                <span className="report-row__value">{report.walkInCheckedIn}</span>
                {pctShown.attendance && (
                  <span className="report-row__pct">{report.walkInShareOfCheckIns}%</span>
                )}
                <span className="report-row__note">of all check-ins</span>
              </div>

              <div className="report-row report-row--muted">
                <span className="report-row__label">Did not attend</span>
                <span className="report-row__value">{report.noShows}</span>
                {pctShown.attendance && (
                  <span className="report-row__pct report-row__pct--none">&mdash;</span>
                )}
                <span className="report-row__note">
                  booked ahead but never checked in
                </span>
              </div>
            </section>

            <section className="report-panel report-panel--wide">
              <header className="report-panel__head">
                <div className="report-panel__heading">
                  <h3>Headcount</h3>
                  <Button
                    size="small"
                    onClick={() => togglePct("headcount")}
                  >
                    {pctShown.headcount ? "Hide %" : "Show %"}
                  </Button>
                </div>
                <p className="report-panel__note">
                  People, not registrations &mdash; one booking admits a whole
                  party
                </p>
              </header>

              <div className="report-row report-row--total">
                <span className="report-row__label">Attended</span>
                <span className="report-row__value">
                  {report.people.attendedTotal}
                </span>
                {pctShown.headcount && (
                  <span className="report-row__pct">{report.people.turnoutRate}%</span>
                )}
                <span className="report-row__note">
                  of {report.people.registeredTotal} people registered
                </span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Adults</span>
                <span className="report-row__value">
                  {report.people.attendedAdults}
                </span>
                {pctShown.headcount && (
                  <span className="report-row__pct">{report.people.adultShare}%</span>
                )}
                <span className="report-row__note">
                  of those who attended &middot;{" "}
                  {report.people.registeredAdults} registered
                </span>
              </div>

              <div className="report-row">
                <span className="report-row__label">Children</span>
                <span className="report-row__value">
                  {report.people.attendedChildren}
                </span>
                {pctShown.headcount && (
                  <span className="report-row__pct">{report.people.childShare}%</span>
                )}
                <span className="report-row__note">
                  of those who attended &middot;{" "}
                  {report.people.registeredChildren} registered
                </span>
              </div>
            </section>
          </div>

          {/*
            * Check-in timeline, from the activity log rather than checkin_at,
            * which a repeat scan overwrites. Shown in venue time - the people
            * reading this are in Houston, and a Manila clock would put the
            * evening rush on the wrong day.
            */}
          <section className="checkin-timeline">
            <header className="checkin-timeline__head">
              {/*
                * The interval buttons are a sibling of the toggle, not inside
                * it: a button group nested in a button is invalid markup and
                * the clicks would fight each other.
                */}
              <div>
                <h3>Check-ins over time</h3>
                <p className="checkin-timeline__note">
                  {checkinTimeline.total} scan
                  {checkinTimeline.total === 1 ? "" : "s"}
                  {repeatScans > 0 ? ` (${repeatScans} repeat)` : ""}, shown in
                  venue time (America/Chicago)
                  {checkinTimeline.busiest
                    ? ` \u00b7 busiest ${checkinTimeline.busiest.label} with ${checkinTimeline.busiest.count}`
                    : ""}
                </p>
              </div>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={checkinInterval}
                onChange={(e, value) => value && setCheckinInterval(value)}
              >
                {CHECKIN_INTERVALS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </header>

            {checkinTimelineChart.hasData ? (
              <div className="checkin-timeline__chart">
                <MetricChart
                  data={checkinTimelineChart.data}
                  options={checkinTimelineChart.options}
                  type={"bar"}
                />
              </div>
            ) : (
              <p className="checkin-timeline__empty">
                No check-ins recorded for this event yet.
              </p>
            )}
          </section>
            </div>
          )}
        </section>
      </Stack>
    </div>
  );
}

export default EventMetrics;
